import type { PostgrestError } from '@supabase/supabase-js';

/**
 * Ukuran halaman default. Supabase / PostgREST membatasi jumlah baris per request
 * (default `max-rows = 1000`). Query yang mengembalikan lebih dari itu akan
 * **dipotong diam-diam tanpa error** — nilai yang hilang membuat rata-rata,
 * peringkat, progress, dan hasil export jadi salah.
 */
export const SUPABASE_PAGE_SIZE = 1000;

/** Batas keamanan: 1000 halaman × 1000 baris = 1 juta baris. */
const MAX_PAGES = 1000;

interface FetchAllRowsOptions {
    /** Ukuran halaman; default 1000. */
    pageSize?: number;
    /**
     * 'throw' (default) → error Supabase dilempar ke pemanggil.
     * 'warn' → error dicatat ke console dan data yang sudah terkumpul dikembalikan.
     */
    onError?: 'throw' | 'warn';
    /** Label untuk pesan console saat onError = 'warn'. */
    label?: string;
}

/**
 * Mengambil **seluruh** baris dari sebuah query Supabase dengan paginasi
 * otomatis, sehingga tidak ada data yang terpotong di batas 1000 baris.
 *
 * PENTING: query **wajib** punya `.order(...)` yang deterministik
 * (mis. `order('id')` atau `order('santri_id')`). Tanpa urutan yang stabil,
 * PostgREST boleh mengembalikan urutan berbeda antar halaman sehingga bisa
 * terjadi baris ganda / baris terlewat.
 *
 * Contoh:
 * ```ts
 * const rows = await fetchAllRows<NilaiRow>((from, to) =>
 *     supabase
 *         .from('nilai_akademik')
 *         .select('santri_id, mapel_id, kognitif, psikomotor, afektif')
 *         .eq('tahun_ajaran', tahunAjaran)
 *         .ilike('semester', `%${semester}%`)
 *         .order('id')
 *         .range(from, to)
 * );
 * ```
 */
export async function fetchAllRows<T = any>(
    buildQuery: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: PostgrestError | null }>,
    options: FetchAllRowsOptions = {}
): Promise<T[]> {
    const { pageSize = SUPABASE_PAGE_SIZE, onError = 'throw', label = '' } = options;

    const rows: T[] = [];
    let from = 0;

    for (let page = 0; page < MAX_PAGES; page++) {
        const { data, error } = await buildQuery(from, from + pageSize - 1);

        if (error) {
            if (onError === 'throw') throw error;
            console.error(`[fetchAllRows] ${label || 'query'} gagal:`, error);
            break;
        }

        // Tidak ada baris lagi.
        if (!data || data.length === 0) break;

        rows.push(...data);

        // Halaman terakhir belum penuh → sudah selesai.
        if (data.length < pageSize) break;

        from += pageSize;
    }

    return rows;
}
